<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  RrButton,
  RrInput,
  RrModal,
  RrRadio,
  RrRadioGroup,
  RrSelect,
  RrSpin,
  RrTag
} from '@xjlv2/ui'
import type { ApiClient } from '@xjlv2/api-client'
import { ExternalLink, Sparkles } from '@lucide/vue'
import { createAiPollingController } from './materialCenterUtils'
import type {
  AiMaterialCapability,
  AiMaterialGeneration,
  AiProviderConfig,
  AiProviderModel,
  ApplyAiMaterialResult,
  GenerateAiMaterialRequest,
  MaterialType,
  PageResult,
  SaasMaterial
} from '../../types'

const props = withDefaults(
  defineProps<{
    createApiClient: () => ApiClient
    moduleCode: string
    material: SaasMaterial | null
    initialMode?: 'editor' | 'providers'
    canRead: boolean
    canEdit: boolean
    canManageProviders: boolean
  }>(),
  {
    initialMode: 'editor'
  }
)

const emit = defineEmits<{
  close: []
  applied: []
  notice: [message: string]
  error: [message: string]
  manageProviders: []
}>()

type OperationOption = { value: string; label: string; description: string; prompt: string }

const capabilityLabels: Record<AiMaterialCapability, string> = {
  TEXT: '文稿',
  IMAGE: '图片',
  AUDIO: '音频',
  VIDEO: '视频'
}

const materialCapability: Record<MaterialType, AiMaterialCapability> = {
  ARTICLE: 'TEXT',
  IMAGE: 'IMAGE',
  AUDIO: 'AUDIO',
  VIDEO: 'VIDEO'
}

const operations: Record<MaterialType, OperationOption[]> = {
  IMAGE: [
    { value: 'GENERATE', label: '创意生成', description: '基于构图与光影描述创作全新画面', prompt: '生成一张主体突出、光影质感出众、符合商业规范的高清图像。' }
  ],
  ARTICLE: [
    { value: 'REWRITE', label: '改写', description: '按目标语气重写正文', prompt: '在保持事实准确的前提下改写正文，使表达更清晰、有吸引力。' },
    { value: 'POLISH', label: '润色', description: '提升语言质量与可读性', prompt: '润色正文，修正语病，统一语气和术语，不增加未经确认的信息。' },
    { value: 'SUMMARIZE', label: '摘要', description: '提炼核心信息', prompt: '提炼正文核心信息，生成结构清晰、可直接发布的摘要。' },
    { value: 'TRANSLATE', label: '翻译', description: '翻译并保持原有结构', prompt: '将正文翻译为目标语言，保留标题、段落结构和专有名词。' }
  ],
  AUDIO: [],
  VIDEO: []
}

const promptPresets = ['商业摄影质感', '影棚柔光', '主体居中特写', '极简纯色背景', '高对比电影感']

const providers = ref<AiProviderConfig[]>([])
const loadingProviders = ref(false)
const generating = ref(false)
const applying = ref(false)
const generation = ref<AiMaterialGeneration | null>(null)
const selectedProviderId = ref<number | null>(null)
const selectedModel = ref('')
const selectedOperation = ref('')
const prompt = ref('')
const parameters = ref({
  temperature: 0.7,
  aspectRatio: '1:1',
  durationSeconds: 10,
  voice: '自然女声',
  language: '简体中文'
})
const applyMode = ref<'UPDATE' | 'NEW_VERSION'>('UPDATE')
const versionName = ref('')
const historyGenerations = ref<AiMaterialGeneration[]>([])
const loadingHistory = ref(false)
const previewViewMode = ref<'GENERATED' | 'ORIGINAL'>('GENERATED')
const elapsedSeconds = ref(0)
let timerHandle: number | undefined
let isDisposed = false
let pollTimer: number | undefined
const pollingController = createAiPollingController()

const materialType = computed<MaterialType>(() => props.material?.type ?? 'IMAGE')
const availableOperations = computed(() => operations[materialType.value])
const availableProviders = computed(() => providers.value.filter((provider) =>
  provider.enabled && providerModelsForCapability(provider, materialCapability[materialType.value]).length > 0
))
const selectedProvider = computed(() => providers.value.find((provider) => provider.id === selectedProviderId.value) ?? null)
const availableModels = computed(() => selectedProvider.value
  ? providerModelsForCapability(selectedProvider.value, materialCapability[materialType.value]).map((model) => model.name)
  : [])

const providerOptions = computed(() =>
  availableProviders.value.map((p) => ({
    value: p.id,
    label: `${p.name}${p.isDefault ? '（默认）' : ''}`
  }))
)

const modelOptions = computed(() =>
  availableModels.value.map((m) => ({
    value: m,
    label: m
  }))
)

const aspectRatioOptions = [
  { value: '1:1', label: '1:1 (正方形)' },
  { value: '16:9', label: '16:9 (横屏)' },
  { value: '4:3', label: '4:3 (标准横屏)' },
  { value: '9:16', label: '9:16 (竖屏)' }
]

const temperatureOptions = [
  { value: 0.3, label: '0.3 - 精确严谨' },
  { value: 0.7, label: '0.7 - 标准平衡' },
  { value: 1.0, label: '1.0 - 创意发散' }
]

const languageOptions = [
  { value: '简体中文', label: '简体中文' },
  { value: '繁体中文', label: '繁体中文' },
  { value: 'English', label: 'English' },
  { value: '日本語', label: '日本語' }
]

const modalTitle = computed(() => {
  return props.material ? `AI 创意工坊 · ${props.material.name}` : 'AI 创意工坊'
})

const canGenerate = computed(() => Boolean(
  props.canEdit && props.material && selectedProviderId.value != null && selectedModel.value && selectedOperation.value && prompt.value.trim() && !generating.value
))

const canApply = computed(() => Boolean(
  props.material && generation.value && generation.value.status === 'COMPLETED' && !applying.value
))

const generateDisabledReason = computed(() => {
  if (!props.canEdit) return '您暂无编辑权限'
  if (!selectedProviderId.value) return '请先选择 AI 提供商'
  if (!selectedModel.value) return '请选择可用的推理模型'
  if (!prompt.value.trim()) return '请输入编辑要求/提示词'
  return ''
})

const currentMediaUrl = computed(() => {
  if (generation.value?.capability === 'IMAGE') {
    return previewViewMode.value === 'ORIGINAL'
      ? (props.material?.coverUrl || props.material?.fileUrl)
      : generation.value?.resultUrl
  }
  return generation.value?.resultUrl || props.material?.fileUrl
})

const currentPhaseTip = computed(() => {
  const sec = elapsedSeconds.value
  if (sec < 2.5) return '正在分析提示词与参数配置...'
  if (sec < 7) return `正在调度 ${selectedModel.value || 'AI 模型'} 算力推理...`
  if (sec < 14) return '正在渲染生成高分辨率图像细节...'
  return '正在同步并校验最终生成结果...'
})

function materialTypeLabel(type: MaterialType) {
  return capabilityLabels[materialCapability[type]]
}

function inferModelCapability(name: string): AiMaterialCapability {
  const value = name.toLowerCase()
  if (value.includes('audio') || value.includes('tts') || value.includes('speech') || value.includes('voice')) return 'AUDIO'
  if (value.includes('happyhorse') || value.includes('video') || /-(i2v|t2v|r2v)/.test(value)) return 'VIDEO'
  if (value.includes('image') || value.startsWith('wan') || value.includes('seedream') || value.includes('flux')) return 'IMAGE'
  return 'TEXT'
}

function providerModelCatalog(provider: AiProviderConfig): AiProviderModel[] {
  if (provider.modelCatalog?.length) {
    return provider.modelCatalog.map((model) => ({ ...model, isDefault: Boolean(model.isDefault) }))
  }
  const singleCapability = provider.capabilities.length === 1 ? provider.capabilities[0] : undefined
  return provider.models.map((name, idx) => ({
    name,
    capability: singleCapability ?? inferModelCapability(name),
    isDefault: idx === 0
  }))
}

function providerModelsForCapability(provider: AiProviderConfig, capability: AiMaterialCapability) {
  return providerModelCatalog(provider).filter((model) => model.capability === capability)
}

function appendPrompt(tag: string) {
  if (!prompt.value.includes(tag)) {
    prompt.value = prompt.value ? `${prompt.value.trim()}，${tag}` : tag
  }
}

function openInNewTab(url?: string | null) {
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

function copyText(text: string) {
  if (!text) return
  void navigator.clipboard.writeText(text).then(() => {
    emit('notice', '已复制到剪贴板')
  })
}

function startTimer() {
  stopTimer()
  elapsedSeconds.value = 0
  timerHandle = window.setInterval(() => {
    elapsedSeconds.value = Math.round((elapsedSeconds.value + 0.2) * 10) / 10
  }, 200)
}

function stopTimer() {
  if (timerHandle != null) {
    window.clearInterval(timerHandle)
    timerHandle = undefined
  }
}

function cancelGeneration() {
  stopPolling()
  stopTimer()
  generating.value = false
  emit('notice', '已取消当前 AI 任务等待')
}

function resetEditor() {
  stopPolling()
  stopTimer()
  const operation = availableOperations.value[0]
  selectedOperation.value = operation?.value ?? ''
  prompt.value = operation?.prompt ?? ''
  applyMode.value = 'UPDATE'
  versionName.value = props.material ? `${props.material.name} AI 版本` : ''
  const preferred = availableProviders.value.find((provider) => provider.isDefault) ?? availableProviders.value[0]
  selectedProviderId.value = preferred?.id ?? null
  const preferredModel = preferred
    ? (providerModelsForCapability(preferred, materialCapability[materialType.value]).find((m) => m.isDefault)?.name ?? preferred.models[0])
    : ''
  selectedModel.value = preferredModel ?? ''
  previewViewMode.value = 'GENERATED'
  void loadGenerations()
}

function selectOperation(operation: OperationOption) {
  if (generating.value) {
    emit('error', '当前有正在执行的生成任务，请等待完成后再切换操作')
    return
  }
  selectedOperation.value = operation.value
  prompt.value = operation.prompt
}

async function loadGenerations() {
  if (!props.material) return
  loadingHistory.value = true
  try {
    const res = await props.createApiClient().get<PageResult<AiMaterialGeneration>>(
      `/api/saas/system/materials/${props.material.id}/ai/generations?pageNo=1&pageSize=10`
    )
    if (isDisposed) return
    historyGenerations.value = res.records || []
    const latest = historyGenerations.value[0]
    if (latest && (!generation.value || generation.value.id === latest.id)) {
      generation.value = latest
      if (latest.status === 'RUNNING') {
        generating.value = true
        startTimer()
        schedulePoll(latest.id)
      }
    }
  } catch {
    // non-blocking
  } finally {
    if (!isDisposed) loadingHistory.value = false
  }
}

function selectHistory(item: AiMaterialGeneration) {
  generation.value = item
  previewViewMode.value = 'GENERATED'
  if (item.status === 'RUNNING') {
    generating.value = true
    startTimer()
    schedulePoll(item.id)
  } else {
    generating.value = false
    stopTimer()
    stopPolling()
  }
}

async function loadProviders() {
  loadingProviders.value = true
  try {
    providers.value = await props.createApiClient().get<AiProviderConfig[]>(
      '/api/saas/system/ai/providers'
    )
    if (props.material) resetEditor()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : 'AI 提供商加载失败')
  } finally {
    loadingProviders.value = false
  }
}

function changeProvider() {
  const provider = selectedProvider.value
  const preferredModel = provider
    ? (providerModelsForCapability(provider, materialCapability[materialType.value]).find((m) => m.isDefault)?.name ?? availableModels.value[0])
    : ''
  selectedModel.value = preferredModel ?? ''
}

function requestParameters() {
  if (materialType.value === 'IMAGE') return { aspectRatio: parameters.value.aspectRatio }
  if (materialType.value === 'ARTICLE') return { temperature: parameters.value.temperature, language: parameters.value.language }
  return {}
}

async function generate() {
  if (!props.material || !canGenerate.value || selectedProviderId.value == null) return
  generating.value = true
  startTimer()
  try {
    const payload: GenerateAiMaterialRequest = {
      providerId: selectedProviderId.value,
      model: selectedModel.value,
      operation: selectedOperation.value,
      prompt: prompt.value.trim(),
      parameters: requestParameters()
    }
    generation.value = await props.createApiClient().post<AiMaterialGeneration>(
      `/api/saas/system/materials/${props.material.id}/ai/generate`,
      payload
    )
    previewViewMode.value = 'GENERATED'
    if (generation.value.status === 'RUNNING') schedulePoll(generation.value.id)
    else {
      generating.value = false
      stopTimer()
    }
    void loadGenerations()
  } catch (err) {
    generating.value = false
    stopTimer()
    emit('error', err instanceof Error ? err.message : 'AI 生成任务提交失败')
  }
}

function stopPolling() {
  pollingController.stop()
  if (pollTimer != null) {
    window.clearTimeout(pollTimer)
    pollTimer = undefined
  }
}

function schedulePoll(generationId: number) {
  stopPolling()
  pollingController.start(generationId)
  pollTimer = window.setTimeout(() => void pollGeneration(generationId), 1800)
}

async function pollGeneration(generationId: number) {
  if (isDisposed || !pollingController.isActive(generationId) || !props.material) return
  try {
    const result = await props.createApiClient().get<AiMaterialGeneration>(
      `/api/saas/system/materials/ai/generations/${generationId}`
    )
    if (isDisposed || !pollingController.isActive(generationId)) return
    if (generation.value?.id === generationId) {
      generation.value = result
    }
    if (result.status === 'RUNNING') {
      schedulePoll(generationId)
    } else {
      generating.value = false
      stopTimer()
      pollingController.stop()
      void loadGenerations()
    }
  } catch (err) {
    if (isDisposed || !pollingController.isActive(generationId)) return
    generating.value = false
    stopTimer()
    pollingController.stop()
    emit('error', err instanceof Error ? err.message : 'AI 生成结果查询失败')
  }
}

async function applyGeneration() {
  if (!props.material || !generation.value || generation.value.status !== 'COMPLETED') return
  applying.value = true
  try {
    await props.createApiClient().post<ApplyAiMaterialResult>(
      `/api/saas/system/materials/ai/generations/${generation.value.id}/apply`,
      {
        mode: applyMode.value,
        name: applyMode.value === 'NEW_VERSION' ? versionName.value.trim() || undefined : undefined
      }
    )
    emit('notice', applyMode.value === 'UPDATE' ? 'AI 生成结果已应用到素材。' : 'AI 结果已保存为新素材版本。')
    emit('applied')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : 'AI 结果应用失败')
  } finally {
    applying.value = false
  }
}

watch(selectedProviderId, changeProvider)
watch(() => props.material?.id, resetEditor)

onMounted(async () => {
  isDisposed = false
  if (props.canRead || props.canManageProviders) {
    await loadProviders()
  }
  if (props.canRead && props.material) {
    await loadGenerations()
  }
})

onUnmounted(() => {
  isDisposed = true
  stopPolling()
  stopTimer()
})
</script>

<template>
  <RrModal
    :open="true"
    :title="modalTitle"
    :width="1060"
    :mask-closable="false"
    centered
    class="ai-studio-modal-dialog"
    @cancel="emit('close')"
  >
    <div class="ai-studio-container">
      <!-- 1. 左侧：主视觉画布与成果展台 (Canvas / Stage) -->
      <section class="studio-canvas-stage">
        <!-- 画布顶部工具条 -->
        <div class="stage-toolbar">
          <div class="stage-title-wrap">
            <span class="stage-material-name">{{ material?.name || '素材' }}</span>
            <RrTag v-if="material" size="small">{{ materialTypeLabel(material.type) }}</RrTag>
            <RrTag
              v-if="generation"
              size="small"
              :color="generation.status === 'COMPLETED' ? 'success' : generation.status === 'FAILED' ? 'error' : 'processing'"
            >
              {{ generation.status }}
            </RrTag>
          </div>

          <div class="stage-actions">
            <!-- 图片对比切换 -->
            <div
              v-if="generation?.capability === 'IMAGE' && generation.resultUrl"
              class="compare-pill-group"
            >
              <RrButton
                size="small"
                :type="previewViewMode === 'GENERATED' ? 'primary' : 'default'"
                @click="previewViewMode = 'GENERATED'"
              >
                AI 效果
              </RrButton>
              <RrButton
                size="small"
                :type="previewViewMode === 'ORIGINAL' ? 'primary' : 'default'"
                @click="previewViewMode = 'ORIGINAL'"
              >
                原图
              </RrButton>
            </div>

            <!-- 新标签页打开 -->
            <RrButton
              v-if="currentMediaUrl"
              size="small"
              :icon="ExternalLink"
              @click="openInNewTab(currentMediaUrl)"
            >
              高清图
            </RrButton>
          </div>
        </div>

        <!-- 画布主体展示区 -->
        <div class="stage-viewport">
          <!-- 正在生成：生动等待态 -->
          <div v-if="generating" class="stage-waiting-overlay">
            <div class="waiting-spin-ring">
              <RrSpin size="large" />
            </div>
            <div class="waiting-counter">已耗时 {{ elapsedSeconds.toFixed(1) }}s</div>
            <div class="waiting-phase-tip">{{ currentPhaseTip }}</div>
            <div class="waiting-cancel-wrap">
              <RrButton size="small" danger @click="cancelGeneration">
                取消生成
              </RrButton>
            </div>
          </div>

          <!-- 生成结果展示 -->
          <div v-else-if="generation" class="stage-media-display">
            <!-- 临时预览地址转存警告 -->
            <div
              v-if="generation.capability === 'IMAGE' && generation.persisted === false"
              class="persist-notice-banner"
            >
              <span>⚠️ 当前为模型临时预览地址，点击下方【保存并应用】将自动转存入库</span>
            </div>

            <!-- 图片渲染 -->
            <template v-if="generation.capability === 'IMAGE'">
              <img
                v-if="previewViewMode === 'ORIGINAL'"
                :src="(material?.coverUrl || material?.fileUrl) ?? undefined"
                :alt="`${material?.name || ''} (原图)`"
                class="stage-image"
              />
              <img
                v-else
                :src="generation.resultUrl ?? undefined"
                alt="AI 生成效果"
                class="stage-image"
              />
            </template>

            <!-- 视频渲染 -->
            <video
              v-else-if="generation.capability === 'VIDEO' && generation.resultUrl"
              :src="generation.resultUrl"
              controls
              class="stage-video"
            />

            <!-- 音频渲染 -->
            <audio
              v-else-if="generation.capability === 'AUDIO' && generation.resultUrl"
              :src="generation.resultUrl"
              controls
              class="stage-audio"
            />

            <!-- 文稿正文展示 -->
            <div v-else class="stage-article-container">
              <div class="article-result-box">
                <div class="article-header">
                  <span>{{ generation.operation }} 结果</span>
                  <RrButton size="small" @click="copyText(generation.resultText || generation.message || '')">复制文本</RrButton>
                </div>
                <div class="article-scroll-text">
                  {{ generation.resultText || generation.message }}
                </div>
              </div>
            </div>
          </div>

          <!-- 初始引导态（显示原图或素材预览） -->
          <div v-else class="stage-idle-state">
            <img
              v-if="material?.type === 'IMAGE' && (material.coverUrl || material.fileUrl)"
              :src="(material.coverUrl || material.fileUrl) ?? undefined"
              :alt="material.name"
              class="stage-image idle-faded"
            />
            <div class="idle-center-card">
              <Sparkles :size="32" class="idle-icon" />
              <div class="idle-title">AI 创意增强画布</div>
              <div class="idle-desc">在右侧配置生成要求并点击【生成预览】，即可在此对比查看成果</div>
            </div>
          </div>
        </div>

        <!-- 画布底部：历史快照传送带 -->
        <div v-if="historyGenerations.length > 0" class="stage-history-reel">
          <span class="history-label">历史任务:</span>
          <div class="history-scroll-box">
            <div
              v-for="item in historyGenerations"
              :key="item.id"
              class="history-chip"
              :class="{ active: generation?.id === item.id }"
              role="button"
              tabindex="0"
              @click="selectHistory(item)"
              @keydown.enter="selectHistory(item)"
            >
              <span class="history-task-id">#{{ item.id }}</span>
              <span class="history-op">{{ item.operation }}</span>
              <RrTag
                size="small"
                :color="item.status === 'COMPLETED' ? 'success' : item.status === 'FAILED' ? 'error' : 'processing'"
              >
                {{ item.status }}
              </RrTag>
            </div>
          </div>
        </div>
      </section>

      <!-- 2. 右侧：AI 操作控制面板 (Inspector / Panel) -->
      <aside class="studio-controls-inspector">
        <!-- 操作模式分段按钮 -->
        <div class="control-section">
          <div class="section-label">处理类型</div>
          <div class="operations-pill-list">
            <div
              v-for="op in availableOperations"
              :key="op.value"
              class="operation-pill-item"
              :class="{ active: selectedOperation === op.value }"
              role="button"
              tabindex="0"
              @click="selectOperation(op)"
              @keydown.enter="selectOperation(op)"
            >
              <span class="op-pill-name">{{ op.label }}</span>
              <span class="op-pill-desc">{{ op.description }}</span>
            </div>
          </div>
        </div>

        <!-- AI 提供商与模型 -->
        <div class="control-section">
          <div class="form-dual-row">
            <div class="form-field-col">
              <label class="field-caption">AI 提供商</label>
              <RrSelect
                v-model:value="selectedProviderId"
                :options="providerOptions"
                placeholder="选择服务商"
                size="middle"
              />
            </div>
            <div class="form-field-col">
              <label class="field-caption">模型</label>
              <RrSelect
                v-model:value="selectedModel"
                :options="modelOptions"
                placeholder="选择模型"
                size="middle"
              />
            </div>
          </div>
        </div>

        <!-- 提示词 / 编辑要求 -->
        <div class="control-section flex-grow">
          <div class="section-label-row">
            <span class="section-label">编辑要求 / 提示词</span>
            <span class="char-count">{{ prompt.length }} 字</span>
          </div>
          <RrInput
            v-model:value="prompt"
            type="textarea"
            :rows="5"
            placeholder="描述希望 AI 如何处理当前素材，如主体特征、色彩风格、画面构成等..."
          />
          <!-- 快捷提示词词签 -->
          <div v-if="material?.type === 'IMAGE'" class="quick-tags-row">
            <span class="quick-tags-label">推荐词:</span>
            <span
              v-for="tag in promptPresets"
              :key="tag"
              class="quick-tag"
              role="button"
              tabindex="0"
              @click="appendPrompt(tag)"
              @keydown.enter="appendPrompt(tag)"
            >
              +{{ tag }}
            </span>
          </div>
        </div>

        <!-- 参数调节 -->
        <div class="control-section">
          <div class="form-dual-row">
            <div v-if="material?.type === 'IMAGE'" class="form-field-col">
              <label class="field-caption">画面比例</label>
              <RrSelect
                v-model:value="parameters.aspectRatio"
                :options="aspectRatioOptions"
                size="middle"
              />
            </div>
            <div v-if="material?.type === 'ARTICLE'" class="form-field-col">
              <label class="field-caption">发散度 (Temperature)</label>
              <RrSelect
                v-model:value="parameters.temperature"
                :options="temperatureOptions"
                size="middle"
              />
            </div>
            <div v-if="material?.type === 'ARTICLE'" class="form-field-col">
              <label class="field-caption">输出语言</label>
              <RrSelect
                v-model:value="parameters.language"
                :options="languageOptions"
                size="middle"
              />
            </div>
          </div>
        </div>

        <!-- 生成主操作按钮 -->
        <div class="control-generate-action">
          <RrButton
            type="primary"
            size="large"
            block
            :loading="generating"
            :disabled="!canGenerate"
            @click="generate"
          >
            <template #icon><Sparkles :size="16" /></template>
            {{ generating ? 'AI 推理生成中...' : '生成预览' }}
          </RrButton>
          <div v-if="!canGenerate && !generating" class="generate-disabled-hint">
            {{ generateDisabledReason }}
          </div>
        </div>
      </aside>
    </div>

    <!-- 弹框底部标准插槽 -->
    <template #footer>
      <div class="studio-modal-footer">
        <div class="footer-save-strategy">
          <span class="strategy-label">保存方式:</span>
          <RrRadioGroup v-model:value="applyMode">
            <RrRadio value="UPDATE">覆盖更新素材</RrRadio>
            <RrRadio value="NEW_VERSION">另存为新版本</RrRadio>
          </RrRadioGroup>
          <RrInput
            v-if="applyMode === 'NEW_VERSION'"
            v-model:value="versionName"
            size="small"
            placeholder="新版本名称"
            style="width: 160px; margin-left: 8px;"
          />
        </div>

        <div class="footer-actions">
          <RrButton @click="emit('close')">取消</RrButton>
          <RrButton
            type="primary"
            :loading="applying"
            :disabled="!canApply"
            @click="applyGeneration"
          >
            {{ applyMode === 'UPDATE' ? '覆盖更新素材' : '保存为新版本' }}
          </RrButton>
        </div>
      </div>
    </template>
  </RrModal>
</template>

<style scoped>
.ai-studio-container {
  display: flex;
  height: 560px;
  background: var(--rr-color-bg-container);
  border-radius: 8px;
  overflow: hidden;
}

/* 1. 左侧：主视觉画布展台 */
.studio-canvas-stage {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--rr-color-fill-quaternary);
  border-right: 1px solid var(--rr-color-border);
}

.stage-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: var(--rr-color-bg-container);
  border-bottom: 1px solid var(--rr-color-border);
}

.stage-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stage-material-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--rr-color-text-primary);
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.compare-pill-group {
  display: inline-flex;
  border-radius: 6px;
  background: var(--rr-color-fill-secondary);
  padding: 2px;
  gap: 2px;
}

/* 画布主体视口 */
.stage-viewport {
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 16px;
}

/* 生动等待态 */
.stage-waiting-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 24px;
}

.waiting-spin-ring {
  margin-bottom: 4px;
}

.waiting-counter {
  font-size: 18px;
  font-weight: 600;
  color: var(--rr-color-primary);
  letter-spacing: 0.5px;
}

.waiting-phase-tip {
  font-size: 13px;
  color: var(--rr-color-text-muted);
  max-width: 320px;
}

.waiting-cancel-wrap {
  margin-top: 8px;
}

/* 媒体呈现容器 */
.stage-media-display {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.persist-notice-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: var(--rr-color-warning-bg);
  color: var(--rr-color-warning-text);
  font-size: 12px;
  padding: 6px 12px;
  text-align: center;
  border-radius: 4px;
  z-index: 2;
}

.stage-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 4px 16px var(--rr-color-shadow-light);
}

.stage-video {
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
}

.stage-audio {
  width: 80%;
}

.stage-article-container {
  width: 100%;
  height: 100%;
  padding: 12px;
  display: flex;
}

.article-result-box {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--rr-color-bg-container);
  border: 1px solid var(--rr-color-border);
  border-radius: 6px;
  overflow: hidden;
}

.article-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--rr-color-fill-quaternary);
  border-bottom: 1px solid var(--rr-color-border);
  font-size: 13px;
  font-weight: 500;
  color: var(--rr-color-text-primary);
}

.article-scroll-text {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  color: var(--rr-color-text-primary);
  white-space: pre-wrap;
}

/* 初始引导态 */
.stage-idle-state {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stage-image.idle-faded {
  opacity: 0.35;
  filter: blur(1px);
}

.idle-center-card {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--rr-color-bg-container);
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  padding: 20px 28px;
  box-shadow: 0 4px 20px var(--rr-color-shadow-light);
  text-align: center;
  max-width: 380px;
}

.idle-icon {
  color: var(--rr-color-primary);
}

.idle-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--rr-color-text-primary);
}

.idle-desc {
  font-size: 12px;
  line-height: 1.5;
  color: var(--rr-color-text-muted);
}

/* 历史快照传送带 */
.stage-history-reel {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--rr-color-bg-container);
  border-top: 1px solid var(--rr-color-border);
}

.history-label {
  font-size: 12px;
  color: var(--rr-color-text-muted);
  white-space: nowrap;
}

.history-scroll-box {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 0;
}

.history-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--rr-color-fill-secondary);
  border: 1px solid var(--rr-color-border);
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 12px;
  transition: all 0.15s ease;
}

.history-chip:hover {
  border-color: var(--rr-color-primary);
}

.history-chip.active {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary-bg);
}

.history-task-id {
  font-weight: 500;
  color: var(--rr-color-text-primary);
}

.history-op {
  color: var(--rr-color-text-muted);
}

/* 2. 右侧：AI 操作控制面板 */
.studio-controls-inspector {
  width: 420px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  gap: 14px;
  background: var(--rr-color-bg-container);
  overflow-y: auto;
}

.control-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.control-section.flex-grow {
  flex: 1;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text-primary);
}

.section-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.char-count {
  font-size: 12px;
  color: var(--rr-color-text-muted);
}

.operations-pill-list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.operation-pill-item {
  display: flex;
  flex-direction: column;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid var(--rr-color-border);
  background: var(--rr-color-fill-quaternary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.operation-pill-item:hover {
  border-color: var(--rr-color-primary);
}

.operation-pill-item.active {
  background: var(--rr-color-primary-bg);
  border-color: var(--rr-color-primary);
}

.op-pill-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text-primary);
}

.op-pill-desc {
  font-size: 11px;
  color: var(--rr-color-text-muted);
  margin-top: 2px;
}

.form-dual-row {
  display: flex;
  gap: 12px;
}

.form-field-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-caption {
  font-size: 12px;
  font-weight: 500;
  color: var(--rr-color-text-muted);
}

.quick-tags-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.quick-tags-label {
  font-size: 11px;
  color: var(--rr-color-text-muted);
}

.quick-tag {
  display: inline-block;
  font-size: 11px;
  padding: 2px 6px;
  background: var(--rr-color-fill-secondary);
  border-radius: 4px;
  color: var(--rr-color-text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.quick-tag:hover {
  background: var(--rr-color-fill-tertiary);
  color: var(--rr-color-primary);
}

.control-generate-action {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.generate-disabled-hint {
  font-size: 11px;
  color: var(--rr-color-text-muted);
  text-align: center;
}

/* 弹框底部 Footer */
.studio-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.footer-save-strategy {
  display: flex;
  align-items: center;
  gap: 8px;
}

.strategy-label {
  font-size: 13px;
  color: var(--rr-color-text-muted);
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
